import { inViewport } from '@/helpers';
import { Reward, OfferTypeFilter, CustomerProfile } from '@/models';
import { Routes } from '@/router';
import { AnalyticsEventCategory, AnalyticsEventAction, AnalyticsEventLabel } from '@/services/google/google-analytics.services';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';

@Component({
  name: 'CmgRewardsExchange',
})
export default class CmgRewardsExchange extends Vue {
  profileWatcher?: () => void;
  shouldShowStickyHeader = false;
  shouldShowFloatingTracker = true;
  selectedOfferType: OfferTypeFilter | null = null;

  async mounted(): Promise<void> {
    const countryCode = this.$appStore.state.ui.countryProfile.countryCode;
    const isLoggedIn = await this.$services.customer.waitForCustomerInitialization();
    let referredFromChipotleULandingPage = !!localStorage.getItem('referredFromChipotleULandingPage');

    if (document.referrer) {
      const referrerPath = new URL(document.referrer).pathname;
      if (referrerPath.includes('/chipotle-u-rewards')) {
        referredFromChipotleULandingPage = true;
        localStorage.setItem('referredFromChipotleULandingPage', 'true');
      }
    }

    if (!isLoggedIn) {
      if (countryCode === 'GB') {
        window.location.replace(`${window.location.origin}/offers`);
      } else if (referredFromChipotleULandingPage) {
        this.$router.replace({ name: Routes.SignIn });
      } else {
        window.location.replace(`${window.location.origin}${this.$services.config.getRewardsPath()}`);
      }
      return;
    }

    if (!this.$services.loyalty.isLoyaltyEligible(this.$appStore.state.customer.profile)) {
      this.$router.replace({ name: Routes.Rewards });
      return;
    }

    if (referredFromChipotleULandingPage &&
      !this.$appStore.state.customer.loyalty.isEnrolled) {
      this.$services.ui.setModalVisibility('chipotleUJoinRewardsPromptModal', true, {
        onJoinRewards: async () => {
          try {
            // Setting shouldSubscribeToMarketingSMS to false prevents sending a "subscribe" request and does not unsubscribe the user.
            const enrollmentOptions = {
              customer: this.customer,
              pageTitle: document.title,
              shouldSubscribeToMarketingEmail: this.customer?.country === 'US',
              shouldSubscribeToMarketingSMS: false,
            };

            await this.$services.loyalty.enrollInLoyalty(enrollmentOptions);
            await this.$services.customer.refreshCustomerProfileInfo();

            if (!this.isCustomerStudent) {
              this.$services.ui.setModalVisibility('idMeVerificationModal', true);
            }
          } catch (e) {
            this.$services.logger.error('Something went wrong while enrolling in loyalty', { error: e });
          }
          localStorage.removeItem('referredFromChipotleULandingPage');
        },
        onContinueWithoutJoining: async () => {
          localStorage.removeItem('referredFromChipotleULandingPage');
          this.$router.push({ name: Routes.Rewards });
        }
      });
    }
    else if (referredFromChipotleULandingPage) {
      localStorage.removeItem('referredFromChipotleULandingPage');
      if (!this.isCustomerStudent) {
        this.$services.ui.setModalVisibility('idMeVerificationModal', true);
      } else {
        window.location.href = this.$router.resolve({ name: Routes.Rewards }).href;
        return;
      }
    } else {
      this.$services.reward.getRewardStoreOfferTypes();
      this.$services.reward.getRewardOffers();

      this.profileWatcher = this.$appStore.watch(state => state.customer.profile, this.showExchangeWelcomeModal);
      
      const referredFromChipotleUVerificationPage = this.$appStore.state.ui.referredFromChipotleUVerificationPage;
      const chipotleUFirstTimeVerification = this.$appStore.state.ui.firstTimeChipotleUVerification;

      if (referredFromChipotleUVerificationPage && this.isCustomerStudent) {
        this.$services.ui.setReferredFromChipotleUVerificationPage(false);
        if (chipotleUFirstTimeVerification) {
          this.$services.ui.setModalVisibility('studentRegistrationSuccessModal', true);
          await this.$services.ui.waitForModalToClose('studentRegistrationSuccessModal');
          this.$services.ui.setFirstTimeChipotleUVerification(false);
        } else {
          this.$services.ui.setModalVisibility('chipotleUReverificationSuccessModal', true);
          await this.$services.ui.waitForModalToClose('chipotleUReverificationSuccessModal');
        }
      } else {
        await this.showExchangeWelcomeModal();
        await this.showCyobModal();
        this.$services.ui.setReferredFromChipotleUVerificationPage(false);
        this.$services.ui.setFirstTimeChipotleUVerification(false);
      }
    }

    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardsStore,
      action: AnalyticsEventAction.PageViewed,
      label: AnalyticsEventLabel.RewardStorePageViewed,
    });
    window.addEventListener('scroll', this.onScroll, true);
  }

  destroyed(): void {
    window.removeEventListener('scroll', this.onScroll, true);

    if (this.profileWatcher) {
      this.profileWatcher();
    }
  }

  async showExchangeWelcomeModal(): Promise<void> {
    if (this.customer && !this.customer.hasSeenRewardsExchOnboarding) {
      this.$services.ui.setModalVisibility('rewardsExchangeWelcome', true);

      await this.$services.ui.waitForModalToClose('rewardsExchangeWelcome');

      // This modal should be displayed only once no matter which device or browser the user is on
      this.customer.hasSeenRewardsExchOnboarding = true;
      this.$services.customer.updateCustomer({ hasSeenRewardsExchOnboarding: true });
    }
  }
  
  private async showCyobModal(): Promise<void> {
    if (this.customer) {
      await this.$services.ui.showCyobModal(
        this.customer,
        this.$services.reward,
        this.$services.loyalty,
        this.$services.customer,
        this.$appStore
      );
    }
  }

  get chevronLeftUrl(): string {
    return this.$appStore.state.cms.images.icons.chevronLeft.default;
  }

  get customer(): CustomerProfile | null {
    const profile = this.$appStore.state.customer.profile;
    return profile && !profile.isGuest ? profile : null;
  }

  get loading(): boolean {
    return !this.isCustomerInitialized || this.$appStore.state.ui.enrollingInRewards;
  }

  get isCustomerInitialized(): boolean {
    return this.$services.customer.isLoggedIn() !== undefined;
  }

  get isCustomerStudent(): boolean {
    return this.$appStore.getters.isActiveStudent;
  }

  get isMobile(): boolean {
    return this.$services.ui.isMobile();
  }

  get isServiceOnline(): boolean {
    return !this.$appStore.state.reward.unreachable;
  }

  get offlineMessage(): string {
    return `We're having some trouble getting your points and rewards.`;
  }

  get isRewardsOnline(): boolean {
    return this.$appStore.state.featureFlags.orderingStatus?.isRewardsOnline ?? true;
  }

  get isRewardsExchangeOnline(): boolean {
    return this.$appStore.state.featureFlags.orderingStatus?.isRewardsExchangeOnline ?? true;
  }

  get sadAvocado(): string {
    return this.$appStore.state.cms.images.feedback.sadAvocado.default;
  }

  get availableOfferTypes() {
    //Determining applicable offer types based on the rewards returned from reward store
    return [...new Set<OfferTypeFilter>(this.$appStore.state.reward.rewards.map(item => item.offerType as OfferTypeFilter))];
  }

  get rewards(): Reward[] {
    return this.$appStore.state.reward.rewards.filter(reward => (this.selectedOfferType ? reward.offerType === this.selectedOfferType : true));
  }

  onClickBack() {
    this.$router.push({ name: Routes.Rewards });
  }

  onScroll(): void {
    if (this.isServiceOnline) {
      this.shouldShowFloatingTracker = !inViewport(this.$refs.rewardTracker as Element);
      this.shouldShowStickyHeader = !inViewport(this.$refs.loyaltyProgressHeader as Element);
    }
  }

  toggleFilter(offerType: OfferTypeFilter) {
    this.selectedOfferType = (this.selectedOfferType === offerType) ? null : offerType;

    if (this.selectedOfferType) {
      this.$services.googleAnalytics.sendCustomEvent({
        category: AnalyticsEventCategory.RewardsStore,
        action: offerType + AnalyticsEventAction.CategoryFiltered,
        label: AnalyticsEventLabel.RewardStoreSpecific + offerType,
      });
    }
  }
}
